import {engineConfig, type Dispatch} from '@packages/systems/flux';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {selectComponentCanvasComponentId} from '@packages/systems/component-canvas/designer';
import {symbolRemoved} from '@packages/systems/element-action-creators';
import {createSelector} from 'reselect';
import {selectShowCodeComponentCreator} from './CodeLibrariesSlice';

const CREATOR_PREFIX = 'creator:';
const CANVAS_PREFIX = 'canvas:';
const NONE = 'none';

// Primitive key so the engine's `singleMemo` runs the body only on real
// transitions: `creator:<id>` (blank stub, creator showing), `canvas:<id>`
// (on that component, creator hidden), or `none`.
export const selectAbandonableCreatorKey = createSelector(
  selectComponentCanvasComponentId,
  selectShowCodeComponentCreator,
  (componentId, showCreator) => {
    if (!componentId) return NONE;
    return `${showCreator ? CREATOR_PREFIX : CANVAS_PREFIX}${componentId}`;
  }
);

// Deletes the blank stub if the user leaves the creator without submitting.
// Staying on the same component (`canvas:<armedId>`) means they submitted, so
// keep it; any other transition is a navigate-away. The stub is still blank
// here since code only lands after submit, which unarms the creator first.
export const AbandonedCreatorCleanupEngine = (
  dispatch: Dispatch<ReturnType<typeof symbolRemoved>>
) => {
  let armedId: string | null = null;

  return (key: string) => {
    if (key.startsWith(CREATOR_PREFIX)) {
      armedId = key.slice(CREATOR_PREFIX.length);
      return;
    }

    if (!armedId) return;

    const abandonedId = armedId;
    armedId = null;

    if (key === `${CANVAS_PREFIX}${abandonedId}`) return;

    dispatch(symbolRemoved(abandonedId));
  };
};

export const abandonedCreatorCleanupEngineConfig = engineConfig<
  DesignerStores,
  ReturnType<typeof symbolRemoved>,
  string
>(AbandonedCreatorCleanupEngine, selectAbandonableCreatorKey);
